package fr.frux.feature.site.detail

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import fr.frux.core.common.error.AppError
import fr.frux.core.common.error.asAppError
import fr.frux.core.common.error.isExpectedFailure
import fr.frux.core.common.logging.Logger
import fr.frux.shared.content.SiteLimits
import fr.frux.shared.domain.model.PlantingCount
import fr.frux.shared.domain.model.PlantingDraft
import fr.frux.shared.domain.model.Site
import fr.frux.shared.domain.model.SiteReportKind
import fr.frux.shared.domain.model.Species
import fr.frux.shared.domain.model.SpeciesLookalike
import fr.frux.shared.domain.model.allowsContribution
import fr.frux.shared.domain.repository.HarvestRepository
import fr.frux.shared.domain.repository.SiteRepository
import fr.frux.shared.domain.repository.SpeciesRepository
import kotlinx.coroutines.Job
import kotlinx.coroutines.channels.Channel
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.filter
import kotlinx.coroutines.flow.receiveAsFlow
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch

data class SiteDetailState(
    val site: Site? = null,
    /** Sosies du référentiel, par id d'espèce plantée. Seules les espèces qui en ont figurent. */
    val lookalikes: Map<Long, List<SpeciesLookalike>> = emptyMap(),
    val isLoading: Boolean = true,
    /** `AppError.NotFound` : lieu supprimé ou masqué, rendu comme introuvable et non comme une panne. */
    val error: AppError? = null,
    val message: SiteDetailMessage? = null,
    val reportOpen: Boolean = false,
    val plantingOpen: Boolean = false,
    val deletePrompt: Boolean = false,
    /** Le lieu est à moi si je l'ai ajouté ou si j'en suis le propriétaire déclaré. */
    val isMine: Boolean = false,
    val isSignedIn: Boolean = false,
    /** Recherche de la feuille « Il manque une espèce ? », espèces déjà plantées exclues. */
    val speciesQuery: String = "",
    val speciesResults: List<Species> = emptyList(),
    /** Brouillons gardés jusqu'au succès : rouvrir une feuille après un échec les retrouve. */
    val reportDraft: SiteReportDraft = SiteReportDraft(),
    val plantingChoice: PlantingChoice = PlantingChoice(),
)

data class SiteReportDraft(
    val kind: SiteReportKind? = null,
    val details: String = "",
) {
    /** « Autre chose » exige une précision, les autres motifs se suffisent. */
    val canSend: Boolean
        get() = kind != null && (kind != SiteReportKind.OTHER || details.isNotBlank())
}

data class PlantingChoice(
    val species: Species? = null,
    val count: PlantingCount? = null,
)

/** Retours ponctuels à afficher en snackbar puis consommer. */
sealed interface SiteDetailMessage {
    data object Reported : SiteDetailMessage

    data object PlantingAdded : SiteDetailMessage

    data object Claimed : SiteDetailMessage

    data object AccessRuleChanged : SiteDetailMessage

    data object Unclaimed : SiteDetailMessage

    data class Failed(
        val error: AppError,
    ) : SiteDetailMessage
}

sealed interface SiteDetailEvent {
    data object Deleted : SiteDetailEvent

    data object LoginRequired : SiteDetailEvent
}

/** Fiche d'un lieu de cueillette (ADR 0035), ouverte à l'anonyme. */
class SiteDetailViewModel(
    private val siteId: Long,
    private val siteRepository: SiteRepository,
    private val speciesRepository: SpeciesRepository,
    harvestRepository: HarvestRepository,
    // Provider, pas un snapshot : la fiche survit à une connexion lancée depuis elle-même (#264).
    private val currentUserId: () -> Long? = { null },
) : ViewModel() {
    private val _state = MutableStateFlow(SiteDetailState())
    val state: StateFlow<SiteDetailState> = _state.asStateFlow()

    private val _events = Channel<SiteDetailEvent>(Channel.BUFFERED)
    val events: Flow<SiteDetailEvent> = _events.receiveAsFlow()

    private var loadJob: Job? = null
    private var favoriteJob: Job? = null

    // Le dépôt annonce la suppression sur `changes` : recharger afficherait « introuvable » avant le retour.
    private var deleting = false

    val harvests =
        SiteHarvestsController(
            harvestRepository = harvestRepository,
            siteRepository = siteRepository,
            scope = viewModelScope,
            isSignedIn = { currentUserId() != null },
            hasSite = { _state.value.site != null },
            onSiteRefreshed = ::onSiteLoaded,
        )

    val ownership =
        SiteOwnershipController(
            siteRepository = siteRepository,
            scope = viewModelScope,
            isSignedIn = { currentUserId() != null },
            onLoginRequired = { _events.trySend(SiteDetailEvent.LoginRequired) },
            onMessage = { message -> _state.update { it.copy(message = message) } },
        )

    private val referential =
        SiteSpeciesReferential(speciesRepository, viewModelScope) {
            // Sosies du lieu affiché au retour, pas de celui de la demande : il a pu changer entre-temps.
            _state.update { s -> s.copy(lookalikes = s.site?.let(::lookalikesOf).orEmpty()) }
            refreshResults()
        }

    init {
        load()
        viewModelScope.launch {
            siteRepository.changes.filter { it == siteId && !deleting }.collect { load() }
        }
    }

    fun retry() = load()

    /**
     * Premier affichage : la copie du cache d'abord, la réponse réseau ensuite. Fiche déjà
     * affichée (rechargement après un geste) : réseau seul, car la copie du cache n'a ni favori,
     * ni droits, ni arbres rattachés et la reposer ferait clignoter la fiche le temps de la réponse.
     */
    private fun load() {
        loadJob?.cancel()
        val shown = _state.value.site != null
        loadJob =
            viewModelScope.launch {
                if (shown) {
                    siteRepository.refreshSite(siteId).fold(onSuccess = ::onSiteLoaded, onFailure = ::onLoadFailure)
                } else {
                    _state.update { it.copy(isLoading = true, error = null) }
                    siteRepository.getSite(siteId).collect { result ->
                        result.fold(onSuccess = ::onSiteLoaded, onFailure = ::onLoadFailure)
                    }
                }
            }
    }

    private fun onSiteLoaded(site: Site) {
        _state.update { it.withSite(site).copy(isLoading = false, error = null) }
        if (site.plantings.isNotEmpty()) referential.load()
        harvests.load(siteId)
    }

    /**
     * Optimiste : le cœur bascule tout de suite et revient en arrière si le serveur refuse. Un seul
     * envoi en vol, les appuis suivants sont ignorés jusqu'à la réponse : deux retours en arrière
     * croisés laisseraient sinon le cœur dans l'état inverse du serveur.
     */
    fun toggleFavorite() {
        val site = _state.value.site?.takeIf { favoriteJob?.isActive != true } ?: return
        if (loginRequired()) return
        val target = !site.isFavorite
        _state.update { it.copy(site = site.copy(isFavorite = target)) }
        favoriteJob =
            viewModelScope.launch {
                siteRepository.setFavorite(siteId, target).onFailure { error ->
                    // Retour en arrière de son seul changement : un rechargement a pu poser la vérité serveur.
                    _state.update { s ->
                        if (s.site?.isFavorite == target) s.copy(site = s.site.copy(isFavorite = !target)) else s
                    }
                    onWriteFailure(error)
                }
            }
    }

    fun openReport() {
        if (loginRequired()) return
        val current = _state.value
        if (current.site == null || current.isMine || current.site.reportedByMe) return
        _state.update { it.copy(reportOpen = true) }
    }

    fun onReportDraft(draft: SiteReportDraft) =
        _state.update { it.copy(reportDraft = draft.copy(details = draft.details.take(SiteLimits.REPORT_DETAILS_MAX))) }

    /** « Autre chose » sans précision ne part pas : la feuille reste ouverte sur le champ requis. */
    fun submitReport() {
        if (loginRequired()) return
        val current = _state.value
        val draft = current.reportDraft
        val kind = draft.kind
        if (!current.reportOpen || kind == null || !draft.canSend) return
        // Fermée avant l'envoi : un second appui ne part pas, et le snackbar n'est pas sous la feuille.
        _state.update { it.copy(reportOpen = false) }
        viewModelScope.launch {
            siteRepository
                .reportSite(siteId, kind, draft.details.trim().ifEmpty { null })
                .onSuccess { site ->
                    _state.update {
                        it.withSite(site).copy(message = SiteDetailMessage.Reported, reportDraft = SiteReportDraft())
                    }
                }.onFailure(::onWriteFailure)
        }
    }

    fun openAddPlanting() {
        if (loginRequired()) return
        if (_state.value.site?.allowsContribution() != true) return
        _state.update { it.copy(plantingOpen = true, speciesQuery = "", speciesResults = emptyList()) }
        referential.load()
    }

    fun onSpeciesQuery(value: String) {
        _state.update { it.copy(speciesQuery = value) }
        refreshResults()
    }

    fun onPlantingChoice(choice: PlantingChoice) = _state.update { it.copy(plantingChoice = choice) }

    /** Le serveur rend le lieu complet : il remplace celui de la fiche. */
    fun addPlanting() {
        if (loginRequired()) return
        val current = _state.value
        val species = current.plantingChoice.species
        val count = current.plantingChoice.count
        if (!current.plantingOpen || species == null || count == null) return
        _state.update { it.copy(plantingOpen = false, speciesQuery = "", speciesResults = emptyList()) }
        viewModelScope.launch {
            siteRepository
                .addPlanting(siteId, PlantingDraft(species.id, count))
                .onSuccess { site ->
                    _state.update {
                        it
                            .withSite(
                                site,
                            ).copy(message = SiteDetailMessage.PlantingAdded, plantingChoice = PlantingChoice())
                    }
                }.onFailure(::onWriteFailure)
        }
    }

    fun requestDelete() {
        if (loginRequired()) return
        if (_state.value.site?.canDelete != true) return
        _state.update { it.copy(deletePrompt = true) }
    }

    fun confirmDelete() {
        if (!_state.value.deletePrompt) return
        _state.update { it.copy(deletePrompt = false) }
        deleting = true
        viewModelScope.launch {
            siteRepository
                .deleteSite(siteId)
                .onSuccess { _events.send(SiteDetailEvent.Deleted) }
                .onFailure { error ->
                    deleting = false
                    onWriteFailure(error)
                }
        }
    }

    fun dismissSheets() =
        _state.update {
            it.copy(
                reportOpen = false,
                plantingOpen = false,
                deletePrompt = false,
                speciesQuery = "",
                speciesResults = emptyList(),
            )
        }

    fun consumeMessage() = _state.update { it.copy(message = null) }

    /** Chaque geste d'un anonyme mène à la connexion, la fiche restant lisible sans compte. */
    private fun loginRequired(): Boolean {
        if (currentUserId() != null) return false
        _events.trySend(SiteDetailEvent.LoginRequired)
        return true
    }

    private fun onWriteFailure(error: Throwable) {
        // Refus attendu (réseau, quota, conflit) sous le niveau erreur : le pont Sentry n'en veut pas (#310).
        if (error.isExpectedFailure) {
            Logger.i(TAG, "Geste refusé sur le lieu $siteId : ${error.message}")
        } else {
            Logger.e(TAG, "Échec d'un geste sur le lieu $siteId", error)
        }
        _state.update { it.copy(message = SiteDetailMessage.Failed(error.asAppError())) }
    }

    private fun SiteDetailState.withSite(site: Site): SiteDetailState {
        val userId = currentUserId()
        return copy(
            site = site,
            lookalikes = referential.lookalikesOf(site),
            isMine = site.isOwner || (userId != null && userId == site.createdBy),
            isSignedIn = userId != null,
        )
    }

    private fun refreshResults() {
        _state.update { s ->
            val query = s.speciesQuery.trim()
            val planted =
                s.site
                    ?.plantings
                    .orEmpty()
                    .mapTo(mutableSetOf()) { it.species.id }
            val results =
                if (query.isEmpty()) {
                    emptyList()
                } else {
                    referential.search(query, excluded = planted, max = MAX_RESULTS)
                }
            s.copy(speciesResults = results)
        }
    }

    private fun onLoadFailure(error: Throwable) {
        val appError = error.asAppError()
        if (appError is AppError.NotFound) {
            // Lien vers un lieu supprimé ou masqué : parcours attendu, pas un event Sentry (#175).
            Logger.i(TAG, "Lieu $siteId introuvable ou masqué")
            _state.update { it.copy(site = null, isLoading = false, error = appError) }
            return
        }
        // Le cache servi d'abord reste affiché : un échec réseau ne le recouvre jamais.
        if (_state.value.site != null) {
            Logger.d(TAG, "Rafraîchissement du lieu $siteId impossible, cache conservé : ${error.message}")
            return
        }
        Logger.e(TAG, "Échec du chargement du lieu $siteId", error)
        _state.update { it.copy(isLoading = false, error = appError) }
    }

    private companion object {
        const val TAG = "SiteDetailViewModel"
        const val MAX_RESULTS = 8
    }
}
